import * as Sentry from '@sentry/vue';
import type { App } from 'vue';
import type { Router } from 'vue-router';
import { scrubSentryEvent } from './diagnostics';

/**
 * Sentry setup.
 *
 * Four of these options are corrections rather than additions — see the comments. Everything that
 * decides *what leaves the browser* lives here or in `redact.ts`, so there is one place to audit.
 */
export const initSentry = (app: App, router: Router): void => {
  Sentry.init({
    app,
    dsn: import.meta.env.VITE_APP_SENTRY_DSN,
    // Explicit rather than relying on `dsn: undefined` behaving sensibly: local `.env.local` has no
    // DSN, and `isEnabled()` is what the report dialog checks to pick a submit channel.
    enabled: !!import.meta.env.VITE_APP_SENTRY_DSN,
    environment: import.meta.env.VITE_APP_ENVIRONMENT || 'staging',
    // Without a release the uploaded sourcemaps do not match and every production stack stays
    // minified, which defeats the point of collecting them.
    release: __APP_RELEASE__,
    dist: __APP_COMMIT__,
    // The v9 default, stated out loud: this is a clinical-trial EDC and the setting matters.
    sendDefaultPii: false,
    beforeSend: scrubSentryEvent,
    integrations: [
      Sentry.vueIntegration({
        // `attachProps` defaults to true, which copies `vm.$props` into every Vue error event. In this
        // app those props are CRF fields and case records.
        attachProps: false,
        // We install our own handler so the event id is available synchronously for
        // `captureFeedback({ associatedEventId })`. Sentry's own handler captures inside a
        // `setTimeout` and, with no prior handler set, rethrows into `window.onerror` where the
        // global handlers capture it a second time.
        attachErrorHandler: false
      }),
      // Same reason: `globalHandlers.ts` listens for these itself, both to get the event id and to
      // put something on screen. Leaving Sentry's on would double-report every one. `dedupeIntegration`
      // stays on as a backstop.
      Sentry.globalHandlersIntegration({ onerror: false, onunhandledrejection: false }),
      Sentry.browserTracingIntegration({ router }),
      // These three match the SDK's current defaults, so this is a no-op today. It is written down so
      // that a future "the replay is unreadable, let's unmask" change has to argue with an explicit
      // line instead of silently flipping a default in an EDC.
      Sentry.replayIntegration({
        maskAllText: true,
        maskAllInputs: true,
        blockAllMedia: true,
        networkDetailAllowUrls: [],
        networkCaptureBodies: false
      })
    ],
    // NOTE: 100% tracing and a 10% always-on session replay are both worth revisiting for production
    // — cost for the former, PHI surface for the latter even fully masked. Left as-is here because
    // that is a compliance decision, not a refactor.
    tracesSampleRate: 1.0,
    tracePropagationTargets: ['localhost', /^https:\/\/api-next(-staging)?\.teamscope\.co/],
    replaysSessionSampleRate: 0.1,
    replaysOnErrorSampleRate: 1.0
  });
};
